/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */
import {ActionTree, Commit} from 'vuex';
import {RootState} from '@/store/types';
import {AxiosResponse} from 'axios';
import {api} from '@/services/http';
import {
    AchievementClaimResponseInterface,
    ActiveCommunityChallengeState,
    ActiveCommunityChallengeStateInterface,
    ActiveCommunityChallengeStateResponseInterface, CommunityChallengeAchievementInterface,
} from '@/store/modules/community-challenge/types';

/**
 * Actions for Community Challenge operations.
 *
 * Actions are functions that can be exported and called upon potentially in an asynchronous manner.
 * To add an action export a named const function. e.g. export const fnName = ({ commit }, {id, name}) => { ... }
 *
 * @author Mark Thompson <mthompson@frontier.co.uk>
 */
export const actions: ActionTree<ActiveCommunityChallengeStateInterface, RootState> = {

    /**
     * Fetch current Community Challenge Progress
     * @param {Commit} commit
     * @param {ActiveCommunityChallengeStateInterface} state
     * @param {ActiveCommunityChallengeState} type
     * @returns {Promise<ActiveCommunityChallengeStateInterface>}
     */
    async getCurrentChallenge(
        {commit, state}: {commit: Commit, state: ActiveCommunityChallengeStateInterface},
        type: ActiveCommunityChallengeState,
    ): Promise<ActiveCommunityChallengeStateResponseInterface> {
        try {
            const response: AxiosResponse<ActiveCommunityChallengeStateResponseInterface> = await api.get(
                `api/zoo/challenges/community/progress`,
                {},
            );
            commit('setActiveChallenge', response.data);

            return response.data;
        } catch (e) {
            throw e;
        }
    },

    /**
     * Claim achievement.
     * @param {Commit} commit
     * @param {CommunityChallengeAchievementInterface} achievement
     * @return {Promise<AchievementClaimResponseInterface>}
     */
    async claim({commit}: {commit: Commit}, achievement: CommunityChallengeAchievementInterface):
        Promise<AchievementClaimResponseInterface> {
        return api.put(`${achievement['@id']}/claim`, {})
            .then((response: AxiosResponse<AchievementClaimResponseInterface>) => {
                commit('updateFromClaim', response.data);
                return response.data;
            });
    },
};
